// ============================================================
// ANSICHTSSACHE-AGENT – Der Rueckweg folgt dem Klickweg
// ============================================================
// Der Rueckpfeil im Seitenkopf eines Blattes zeigte fest auf die
// Liste seiner Art: eine Rechnung immer nach `/rechnungen`, auch
// wenn man sie im Projekt angeklickt hatte (Ansage vom
// 22.10.2026: „wenn ich hier auf eine Rechnung klicke, muss ich
// dann über den Zurück-Button wieder dahin kommen … bitte überall
// den Klickweg korrekt verfolgen“).
//
// Darum fuehrt der Browser je Reiter einen KLICKWEG mit: jede
// Adresse, die er aufschlaegt, kommt oben drauf (`AddressMemory`
// im Wurzel-Layout). Der Pfeil eines Blattes geht diesen Weg von
// oben nach unten, ueberspringt alles, was zum Blatt selbst gehoert
// (seine Register), und fuehrt zur ersten Seite davor — samt ihrem
// Suchteil, also mit Filter, Register und offenem Blatt, wie man
// sie verlassen hat. Wer zurueckgeht, ueber den Pfeil oder den
// Zurueck-Knopf des Browsers, kuerzt den Weg bis dorthin; sonst
// fuehrte der Pfeil des Projekts danach wieder in die Rechnung.
//
// Liegt nichts davor (Lesezeichen, neuer Reiter, Neuladen nach
// dem Schliessen), bleibt es beim festen Ziel des Blattes — mit
// dem Filter, mit dem die Liste in diesem Reiter zuletzt offen
// war (Ansage vom 14.10.2026: „sollte der Filter noch aktiv sein,
// das gilt fuer alle Listen"). Dafuer merkt sich der Browser
// zusaetzlich je Pfad den letzten Suchteil.
//
// Gemerkt wird in `sessionStorage`: je Reiter, weil zwei Reiter
// zwei Arbeitsgaenge sind, und nur bis zum Schliessen, weil ein
// Weg von gestern keine Auskunft mehr ist. Eine Adresse und keine
// Liste von Schluesseln: so gilt es fuer jede Seite, auch fuer
// eine, die morgen dazukommt, ohne dass sie etwas dafuer tun muss.
// ============================================================

const STORAGE_KEY = "ans:return-address";
const TRAIL_KEY = "ans:click-trail";
/** Genug fuer jeden Arbeitsgang; aeltere Pfade fallen hinten heraus. */
const MAX_ENTRIES = 80;
/** Tiefer geht niemand zurueck; der Rest faellt unten heraus. */
const MAX_TRAIL = 60;

/**
 * Seiten, die nicht in den Weg gehoeren: wer sich anmeldet, beginnt
 * einen neuen Arbeitsgang, und der Pfeil des ersten Blattes danach
 * darf nicht zur Anmeldung zurueckfuehren.
 */
const RESTART_PATHS = ["/login", "/einladung", "/kennwort"];

export interface TrailEntry {
  path: string;
  /** Suchteil ohne `?`. */
  search: string;
}

/** Pfad → Suchteil ohne `?`. Die Reihenfolge ist die der letzten Nutzung. */
let memory: Map<string, string> | null = null;
let trail: { entries: TrailEntry[]; titles: Map<string, string> } | null = null;
/** Der Pfad, den der Zurueck-Knopf des Browsers gerade aufschlaegt. */
let historyTarget: string | null = null;
const listeners = new Set<() => void>();

function load(): Map<string, string> {
  if (memory) return memory;
  memory = new Map();
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === "object") {
      for (const [path, search] of Object.entries(parsed)) {
        if (typeof search === "string") memory.set(path, search);
      }
    }
  } catch {
    // Kein Speicher (privates Fenster, Sperre): dann eben nur im Speicher.
  }
  return memory;
}

function loadTrail(): NonNullable<typeof trail> {
  if (trail) return trail;
  trail = { entries: [], titles: new Map() };
  try {
    const raw = window.sessionStorage.getItem(TRAIL_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === "object") {
      const { entries, titles } = parsed as { entries?: unknown; titles?: unknown };
      if (Array.isArray(entries)) {
        for (const e of entries) {
          if (e && typeof e.path === "string" && typeof e.search === "string") {
            trail.entries.push({ path: e.path, search: e.search });
          }
        }
      }
      if (titles && typeof titles === "object") {
        for (const [path, title] of Object.entries(titles)) {
          if (typeof title === "string") trail.titles.set(path, title);
        }
      }
    }
  } catch {
    // Wie oben: ohne Speicher gilt der Weg nur bis zum Neuladen.
  }
  return trail;
}

function saveTrail(): void {
  const t = loadTrail();
  try {
    window.sessionStorage.setItem(
      TRAIL_KEY,
      JSON.stringify({ entries: t.entries, titles: Object.fromEntries(t.titles) }),
    );
  } catch {
    // Voll oder gesperrt — der Stand im Speicher gilt trotzdem.
  }
  notify();
}

function notify(): void {
  for (const listener of listeners) listener();
}

/** Merkt sich den Suchteil, mit dem `pathname` gerade offen ist. */
export function rememberAddress(pathname: string, search: string): void {
  recordVisit(pathname, search);
  const map = load();
  if (map.get(pathname) === search) return;
  map.delete(pathname);
  map.set(pathname, search);
  for (const oldest of map.keys()) {
    if (map.size <= MAX_ENTRIES) break;
    map.delete(oldest);
  }
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(map)));
  } catch {
    // Voll oder gesperrt — der Stand im Speicher gilt trotzdem.
  }
  notify();
}

/** Der zuletzt gemerkte Suchteil fuer `pathname`, `null` = nie hier gewesen. */
export function rememberedSearch(pathname: string): string | null {
  return load().get(pathname) ?? null;
}

export function subscribeReturnAddress(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Der Zurueck-Knopf des Browsers (oder `router.back()`) schlaegt
 * gleich `pathname` auf. Der naechste Besuch dort kuerzt den Weg,
 * statt ihn zu verlaengern.
 */
export function markHistoryNavigation(pathname: string): void {
  historyTarget = pathname;
}

function recordVisit(path: string, search: string): void {
  const t = loadTrail();
  const fromHistory = historyTarget === path;
  historyTarget = null;
  const next = RESTART_PATHS.some((p) => path === p || path.startsWith(`${p}/`))
    ? []
    : nextTrail(t.entries, path, search, fromHistory);
  if (next === t.entries) return;
  t.entries = next;
  saveTrail();
}

/** Merkt sich den Titel der Seite, damit der Pfeil sagen kann, wohin er fuehrt. */
export function rememberTitle(pathname: string, title: string): void {
  const t = loadTrail();
  if (t.titles.get(pathname) === title) return;
  t.titles.delete(pathname);
  t.titles.set(pathname, title);
  for (const oldest of t.titles.keys()) {
    if (t.titles.size <= MAX_ENTRIES) break;
    t.titles.delete(oldest);
  }
  saveTrail();
}

/**
 * Der Pfeil wurde gedrueckt: der Weg endet jetzt an `index`, die
 * Seite dort wird gleich wieder die oberste.
 */
export function returnTo(index: number): void {
  const t = loadTrail();
  if (index < 0 || index >= t.entries.length - 1) return;
  t.entries = t.entries.slice(0, index + 1);
  saveTrail();
}

export interface ReturnTarget {
  href: string;
  /** Wohin der Pfeil fuehrt, `null` = unbekannt (dann nur „Zurück“). */
  label: string | null;
  /** Stelle im Klickweg, -1 = festes Ziel des Blattes. */
  index: number;
}

/** Das Ziel des Pfeils auf `pathname` mit dem Stand dieses Reiters. */
export function currentReturnTarget(
  pathname: string,
  back: { href: string; label: string },
): ReturnTarget {
  const t = loadTrail();
  const backPath = back.href.split(/[?#]/)[0];
  return returnTarget(t.entries, t.titles, pathname, back, rememberedSearch(backPath));
}

// ------------------------------------------------------------
// Reine Rechnung — ohne Browser, damit sie sich pruefen laesst.
// ------------------------------------------------------------

/**
 * Der Weg nach dem Besuch von `path`. Dieselbe Seite mit anderem
 * Filter ersetzt den obersten Schritt; der Zurueck-Knopf des
 * Browsers kuerzt bis zum letzten Besuch dieser Seite; alles
 * andere kommt oben drauf. Unveraendert kommt `entries` selbst
 * zurueck.
 */
export function nextTrail(
  entries: TrailEntry[],
  path: string,
  search: string,
  fromHistory: boolean,
): TrailEntry[] {
  const top = entries[entries.length - 1];
  if (top?.path === path) {
    if (top.search === search) return entries;
    return [...entries.slice(0, -1), { path, search }];
  }
  if (fromHistory) {
    for (let i = entries.length - 2; i >= 0; i--) {
      if (entries[i].path === path) return [...entries.slice(0, i), { path, search }];
    }
  }
  return [...entries, { path, search }].slice(-MAX_TRAIL);
}

/**
 * Was zum Blatt auf `pathname` gehoert: das Blatt und seine
 * Register. Das feste Ziel des Pfeils sagt, wo das Blatt anfaengt —
 * `/projekte/abc/aufgaben` mit Ziel `/projekte` gehoert zu
 * `/projekte/abc`. Steht das Blatt nicht unter seinem Ziel
 * (`/hosting/domains/x` → `/hosting/kosten`), ist es nur es selbst.
 */
export function sheetRoot(pathname: string, backPath: string): string {
  if (backPath !== "/" && pathname.startsWith(`${backPath}/`)) {
    const segment = pathname.slice(backPath.length + 1).split("/")[0];
    return `${backPath}/${segment}`;
  }
  return pathname;
}

function within(path: string, root: string): boolean {
  return path === root || path.startsWith(`${root}/`);
}

/** Die Stelle im Weg, an die der Pfeil auf `pathname` zurueckfuehrt, -1 = keine. */
export function findReturn(entries: TrailEntry[], pathname: string, backPath: string): number {
  const root = sheetRoot(pathname, backPath);
  for (let i = entries.length - 1; i >= 0; i--) {
    if (!within(entries[i].path, root)) return i;
  }
  return -1;
}

export function returnTarget(
  entries: TrailEntry[],
  titles: ReadonlyMap<string, string>,
  pathname: string,
  back: { href: string; label: string },
  remembered: string | null,
): ReturnTarget {
  const backPath = back.href.split(/[?#]/)[0];
  const index = findReturn(entries, pathname, backPath);
  if (index < 0) {
    return { href: resolveReturnHref(back.href, remembered), label: back.label, index };
  }
  const from = entries[index];
  // Kam man aus der Liste selbst, heisst sie weiter so, wie das
  // Blatt sie nennt — und traegt genau den Filter, mit dem man sie
  // verlassen hat.
  if (from.path === backPath && matches(back.href, from.search)) {
    return { href: resolveReturnHref(back.href, from.search), label: back.label, index };
  }
  return {
    href: from.search ? `${from.path}?${from.search}` : from.path,
    label: titles.get(from.path) ?? null,
    index,
  };
}

/** Enthaelt `search` alle Angaben aus dem Suchteil von `href`? */
function matches(href: string, search: string): boolean {
  const query = splitOnce(splitOnce(href, "#")[0], "?")[1];
  if (!query) return true;
  const have = new URLSearchParams(search);
  for (const [key, value] of new URLSearchParams(query)) {
    if (!have.getAll(key).includes(value)) return false;
  }
  return true;
}

/**
 * Das feste Ziel des Rueckpfeils mit dem gemerkten Suchteil.
 *
 * Traegt `href` selbst schon einen Suchteil (`/hosting/kosten?art=domain`),
 * ist der eine Zusage: gemerkt wird dann nur uebernommen, wenn er
 * dieselben Angaben enthaelt — sonst fuehrte „Zurück zu Domains" in
 * die Server, weil man zuletzt dort gefiltert hatte.
 */
export function resolveReturnHref(href: string, remembered: string | null): string {
  if (remembered === null) return href;
  if (!matches(href, remembered)) return href;
  const [base, hash] = splitOnce(href, "#");
  const [path] = splitOnce(base, "?");
  const target = remembered ? `${path}?${remembered}` : path;
  return hash !== undefined ? `${target}#${hash}` : target;
}

function splitOnce(value: string, separator: string): [string, string | undefined] {
  const at = value.indexOf(separator);
  return at === -1 ? [value, undefined] : [value.slice(0, at), value.slice(at + 1)];
}
